'use client'

import { useEffect, useState, useRef, useMemo, useCallback, createRef, type ReactNode } from 'react'
import { motion } from 'motion/react'
import AppIcon from '@/components/content/app/AppIcon'
import { Icon } from '@/icons/Icon'
import { cn, toSlug, scrollToSection, getBrandColors, isPopulatedObject } from '@/lib/utils'
import { usePageContext } from '@/components/content/common/PageContext'
import { SectionItem } from '@/types/app'
import type { App, Media } from '@/payload-types'

type TreeProps = {
  branches?: SectionItem[]
  leaves?: SectionItem[][]
}

export type SidebarProps = {
  id?: string | null
  treeHeight: number
  adaptive?: boolean
  offset: number
}

export type RootProps = TreeProps & {
  title: string
  icon?: ReactNode
  overview?: boolean
}

type RootContainerProps = SidebarProps & {
  item: SectionItem
  isLast: boolean
}

export default function Root({ item, treeHeight, offset, isLast }: RootContainerProps) {
  const { activeSection, setSidebarVisible } = usePageContext()

  // Generate the section ID based on the root title to match what Section components create
  const sectionId = item.app ? item.app.slug : item.slug ?? toSlug(item.title || '')

  // Root is active when the active section is exactly this root
  const isRootActive =
    activeSection === sectionId || (!!activeSection && activeSection.startsWith(`${sectionId}-`))

  const [branchesVisible, setBranchesVisible] = useState<boolean>(isRootActive)
  const hasBranches = item.children?.length

  useEffect(() => {
    setBranchesVisible(isRootActive)
  }, [isRootActive])

  const app = item.app as App | null
  const appIcon = app?.icon as Media | undefined
  const appGlyph = app?.glyph as Media | undefined
  const hasIcon = isPopulatedObject<Media>(appIcon) || isPopulatedObject<Media>(appGlyph)

  const { backgroundColor } = getBrandColors(app)

  const handleClick = () => {
    if (sectionId) {
      scrollToSection({ id: sectionId, offset, animate: true, hideHash: true })
    }
    setSidebarVisible(false)
  }

  return (
    <li className={cn('relative list-none overflow-hidden')}>
      <div
        className={cn(
          'group relative flex h-13 bg-zinc-50 text-16 font-medium leading-none dark:bg-white/5 md:h-12 md:text-14',
          isLast ? (hasBranches && branchesVisible ? 'rounded-b-none' : 'rounded-b-2xl') : '',
        )}
      >
        {isRootActive && (
          <motion.div
            layoutId="sidebar-active-highlight"
            className="absolute left-4 top-3.5 z-50 h-6 w-0.5 rounded-full bg-primary-500 md:top-3"
          />
        )}

        <div
          onClick={handleClick}
          className={cn(
            'flex w-full cursor-pointer items-center gap-2 rounded pl-4 pr-10 leading-snug text-gray-950 transition-transform duration-300 ease-in-out',
            isRootActive && 'translate-x-4',
          )}
        >
          {app && hasIcon ? (
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={app.name}
              background={backgroundColor}
              size={20}
            />
          ) : item.icon ? (
            <div className="flex h-5 w-5 items-center justify-center text-gray-500">
              {typeof item.icon === 'string' ? <Icon name={item.icon} /> : item.icon}
            </div>
          ) : null}
          <span className="line-clamp-2 dark:text-white">{item.title}</span>
        </div>

        {hasBranches ? (
          <div
            onClick={() => setBranchesVisible(!branchesVisible)}
            className="ml-auto flex aspect-square h-full cursor-pointer items-center justify-center"
          >
            <Icon
              name="chevron-left"
              size={12}
              className={cn(
                'h-6 w-6 text-dark-300 transition-transform dark:text-white/60',
                branchesVisible ? 'rotate-90' : '-rotate-90',
              )}
            />
          </div>
        ) : null}
      </div>

      {hasBranches ? (
        <>
          {branchesVisible && (
            <Branch
              id={sectionId}
              branches={item.children}
              leaves={item.children?.map((branch) => branch.children || [])}
              activeSection={activeSection}
              treeHeight={treeHeight}
              offset={offset}
              isLast={isLast}
            />
          )}
        </>
      ) : null}
    </li>
  )
}

export function Branch({
  id,
  branches,
  leaves,
  activeSection,
  treeHeight,
  adaptive,
  offset,
  isLast,
}: TreeProps & SidebarProps & { activeSection: string | null; isLast: boolean }) {
  const { setSidebarVisible } = usePageContext()

  const [isBottom, setIsBottom] = useState<boolean>(false)

  const branchScroller = useRef<HTMLUListElement>(null)
  const branchesRef = useMemo(
    () => branches?.map(() => createRef<HTMLLIElement>()) ?? [],
    [branches],
  )

  const handleClick = (slug: string) => {
    scrollToSection({ id: slug, offset, animate: true, hideHash: true })
    setSidebarVisible(false)
  }

  const checkScrollPosition = useCallback(() => {
    if (!branchScroller.current) return

    const { scrollHeight, scrollTop, clientHeight } = branchScroller.current
    setIsBottom(scrollHeight - scrollTop <= clientHeight || scrollHeight <= treeHeight)
  }, [treeHeight])

  useEffect(() => {
    if (branchScroller.current) {
      checkScrollPosition()
    }
  }, [checkScrollPosition])

  useEffect(() => {
    if (branchScroller.current && activeSection && branches) {
      // Find which branch is active based on the section ID
      const selectedIndex = branches.findIndex((branch) => {
        const branchSlug = toSlug(branch.title || '')
        return activeSection === `${id}-${branchSlug}`
      })

      if (selectedIndex === -1) return

      const selectedRef = branchesRef[selectedIndex].current
      const scrollPosition = selectedRef ? selectedRef.offsetTop - 12 : 0

      branchScroller.current.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      })
    }
  }, [activeSection, branches, branchesRef, id])

  return (
    <div
      style={{ height: adaptive ? `${treeHeight}px` : 'auto' }}
      className={cn(
        'hide-scrollbar overflow-hidden border-t border-dark-100 bg-white after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:z-50 after:h-10 after:w-full after:bg-gradient-to-t after:from-white after:to-transparent after:transition-opacity dark:border-white/10 dark:bg-black dark:after:from-black',
        isBottom ? 'after:opacity-0' : 'after:opacity-100',
        isLast && 'rounded-b-2xl',
      )}
    >
      <ul
        ref={branchScroller}
        className="relative flex h-full w-full flex-col overflow-y-scroll px-4 py-3"
        onScroll={checkScrollPosition}
      >
        {branches?.map((branch, index) => {
          const branchApp = branch.app as App | null
          const branchAppIcon = branchApp?.icon as Media | undefined
          const branchAppGlyph = branchApp?.glyph as Media | undefined
          const branchHasIcon =
            isPopulatedObject<Media>(branchAppIcon) || isPopulatedObject<Media>(branchAppGlyph)
          const { backgroundColor } = getBrandColors(branchApp)
          const hasLeaves = leaves && leaves?.[index]?.length > 0
          const branchSlug = branch.slug ?? `${id}-${toSlug(branch.title || '')}`

          // Branch is active if the current section matches exactly or starts with this branch
          const isBranchActive =
            activeSection === branchSlug || activeSection?.startsWith(`${branchSlug}-`)
          const showBranchHighlight =
            isBranchActive && !activeSection?.includes('-', branchSlug.length + 1)

          return (
            <li key={branch.title} ref={branchesRef[index]} className="flex flex-col">
              <div
                className={cn(
                  'relative flex h-10 cursor-pointer items-center pl-4 text-15 transition-colors hover:text-dark-950 md:h-8.5 md:text-14 lg:leading-snug',
                  'after:absolute after:left-0 after:h-full after:w-0.5 after:bg-dark-100 dark:after:bg-white/15',
                  showBranchHighlight
                    ? 'font-medium text-gray-950 dark:text-white'
                    : 'text-gray-600 dark:text-white/60',
                )}
                onClick={() => handleClick(branchSlug)}
              >
                {showBranchHighlight && (
                  <motion.div
                    layoutId="sidebar-active-highlight-branch"
                    className="absolute left-0 top-0 z-50 h-full w-0.5 rounded-full bg-primary-500"
                  />
                )}
                {branchApp && branchHasIcon ? (
                  <AppIcon
                    icon={branchAppIcon}
                    glyph={branchAppGlyph}
                    name={branchApp.name}
                    background={backgroundColor}
                    size={20}
                    className="mr-3.5"
                  />
                ) : null}
                <span className="line-clamp-2">{branch.title}</span>
              </div>

              {hasLeaves && (
                <div className="relative flex flex-col">
                  {leaves?.[index]?.map((leaf) => {
                    const leafSlug = leaf.slug ?? `${id}-${toSlug(leaf.title || '')}`

                    const isLeafActive =
                      activeSection === leafSlug || activeSection?.startsWith(`${leafSlug}-`)
                    const showLeafHighlight =
                      isLeafActive && !activeSection?.includes('-', leafSlug.length + 1)

                    return (
                      <div key={leaf.title} className="relative">
                        {showLeafHighlight && (
                          <motion.div
                            layoutId="sidebar-active-highlight-leaf"
                            className="absolute left-0 top-0 z-50 h-full w-0.5 rounded-full bg-primary-500"
                          />
                        )}
                        <div
                          className={cn(
                            'relative flex h-10 cursor-pointer items-center pl-8 text-15 transition-colors hover:text-dark-950 md:h-8.5 md:text-14',
                            isLeafActive
                              ? 'font-medium text-dark-950 dark:text-white'
                              : 'text-dark-600 dark:text-white/60',
                            'after:absolute after:left-0 after:h-full after:w-0.5 after:bg-dark-100 dark:after:bg-white/15',
                          )}
                          onClick={() => handleClick(leafSlug)}
                        >
                          {leaf.title}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
